import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Button as MuiButton,
  Typography,
} from '@mui/material';
import React from 'react';
import {
  Datagrid,
  ResourceContextProvider,
  TextField,
  useDataProvider,
  useListContext,
  useNotify,
  useRefresh,
} from 'react-admin';
import useCanWrite from '../../../hooks/useCanWrite';
import useIsExerciseEditor from '../../../hooks/useIsExerciseEditor';
import { nameColumnStyling } from '../../../utils/nameFieldSx';
import InfiniteList from '../components/InfiniteList';
import { ParentSelectionFilter } from './filters';

export type ParentChangeConfig = {
  resource: 'ExerciseTypeCategory' | 'ExerciseType' | 'ExerciseBase';
  itemLabel: string;
  parentResource:
    | 'ExerciseTypeCategoryGroup'
    | 'ExerciseTypeCategory'
    | 'ExerciseType';
  parentLabel: string;
};

type ParentPickerRow = { id: string; name?: string | null };

const getReparentUpdateData = (
  id: string,
  parentId: string,
  config: ParentChangeConfig,
): Record<string, unknown> => {
  switch (config.resource) {
    case 'ExerciseTypeCategory':
      return { id, exerciseTypeCategoryGroupId: parentId };
    case 'ExerciseType':
      return { id, exerciseTypeCategoryId: parentId };
    case 'ExerciseBase':
      return { id, exerciseTypeId: parentId };
    default:
      throw new Error(`Unsupported reparent resource: ${config.resource}`);
  }
};

type ChangeParentBulkActionButtonProps = {
  config: ParentChangeConfig;
};

export const ChangeParentBulkActionButton: React.FC<
  ChangeParentBulkActionButtonProps
> = ({ config }) => {
  const isExerciseEditor = useIsExerciseEditor();
  const canWrite = useCanWrite();
  const { selectedIds, onUnselectItems } = useListContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const canManageExercises = canWrite || isExerciseEditor;

  const [isParentPickerOpen, setIsParentPickerOpen] = React.useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const [isProgressOpen, setIsProgressOpen] = React.useState(false);
  const [targetParent, setTargetParent] =
    React.useState<ParentPickerRow | null>(null);
  const [completedCount, setCompletedCount] = React.useState(0);
  const [successCount, setSuccessCount] = React.useState(0);
  const [failureCount, setFailureCount] = React.useState(0);
  const [isUpdating, setIsUpdating] = React.useState(false);

  const selectedCount = selectedIds.length;

  const closeAllDialogs = () => {
    setIsParentPickerOpen(false);
    setIsConfirmOpen(false);
    setIsProgressOpen(false);
    setTargetParent(null);
    setCompletedCount(0);
    setSuccessCount(0);
    setFailureCount(0);
    setIsUpdating(false);
  };

  const handleChooseParent = (record: ParentPickerRow) => {
    setTargetParent(record);
    setIsParentPickerOpen(false);
    setIsConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!targetParent) {
      return;
    }

    setIsConfirmOpen(false);
    setIsProgressOpen(true);
    setCompletedCount(0);
    setSuccessCount(0);
    setFailureCount(0);
    setIsUpdating(true);

    let successfulUpdates = 0;
    let failedUpdates = 0;

    for (const [index, rawId] of selectedIds.entries()) {
      const id = String(rawId);
      try {
        const updateData = getReparentUpdateData(id, targetParent.id, config);
        await dataProvider.update(config.resource, {
          id,
          data: updateData,
          previousData: { id },
        });
        successfulUpdates += 1;
      } catch {
        failedUpdates += 1;
      }
      setCompletedCount(index + 1);
      setSuccessCount(successfulUpdates);
      setFailureCount(failedUpdates);
    }

    setIsUpdating(false);

    if (failedUpdates > 0) {
      notify(
        `Moved ${successfulUpdates} ${config.itemLabel}(s). ${failedUpdates} failed.`,
        { type: 'warning' },
      );
    } else {
      notify(
        `Moved ${successfulUpdates} ${config.itemLabel}(s) to ${targetParent.name ?? targetParent.id}.`,
      );
    }

    closeAllDialogs();
    onUnselectItems();
    refresh();
  };

  if (selectedCount === 0) {
    return null;
  }

  if (!canManageExercises) {
    return null;
  }

  return (
    <>
      <MuiButton onClick={() => setIsParentPickerOpen(true)}>
        Change {config.parentLabel}
      </MuiButton>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={isParentPickerOpen}
        onClose={() => setIsParentPickerOpen(false)}
      >
        <DialogTitle>Select {config.parentLabel}</DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
            <ResourceContextProvider value={config.parentResource}>
              <InfiniteList
                disableSyncWithLocation
                filters={<ParentSelectionFilter />}
                storeKey={`${config.parentResource}-reparent-picker`}
              >
                <Datagrid
                  bulkActionButtons={false}
                  rowClick={(_id, _resource, record) => {
                    handleChooseParent(record as ParentPickerRow);
                    return false;
                  }}
                >
                  <TextField
                    source="name"
                    sortable={false}
                    sx={nameColumnStyling}
                  />
                </Datagrid>
              </InfiniteList>
            </ResourceContextProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setIsParentPickerOpen(false)}>
            Cancel
          </MuiButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isConfirmOpen}
        onClose={() => {
          setIsConfirmOpen(false);
          setTargetParent(null);
        }}
      >
        <DialogTitle>Confirm Move</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Move {selectedCount} {config.itemLabel}(s) to{' '}
            {targetParent?.name ?? targetParent?.id}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton
            onClick={() => {
              setIsConfirmOpen(false);
              setTargetParent(null);
            }}
            disabled={isUpdating}
          >
            Cancel
          </MuiButton>
          <MuiButton onClick={handleConfirm} disabled={isUpdating}>
            Confirm
          </MuiButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isProgressOpen}
        onClose={isUpdating ? undefined : closeAllDialogs}
      >
        <DialogTitle>Moving {config.itemLabel}(s)</DialogTitle>
        <DialogContent sx={{ minWidth: 360 }}>
          <Typography variant="body2" gutterBottom>
            {completedCount}/{selectedCount} processed
          </Typography>
          <LinearProgress
            variant="determinate"
            value={
              selectedCount === 0 ? 0 : (completedCount / selectedCount) * 100
            }
          />
          <Typography variant="body2" sx={{ marginTop: 1 }}>
            Success: {successCount} | Failed: {failureCount}
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={closeAllDialogs} disabled={isUpdating}>
            {isUpdating ? 'Updating...' : 'Close'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
